import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CameraOutlined, PictureOutlined } from "@ant-design/icons";
import CommonIcons from "@base/assets/icons";
import CommonStyles from "@components/CommonStyles";
import useToggle from "@base/hooks/useToggle";
import { decodeQr, decodeQrFromFile } from "@base/helpers/qrScan";
import { isInsuranceQR } from "@base/modules/appointment/regularBookingRules";
import { IHealthInsuranceCard } from "@base/modules/patientInsurance/healthInsurance.interface";
import patientInsuranceService from "@base/modules/patientInsurance/patientInsuranceService";
import { IPatientRecord } from "@base/modules/user/profile/profile.interface";

interface IProps {
  value: IHealthInsuranceCard | null;
  record: IPatientRecord | null;
  onChange: (card: IHealthInsuranceCard | null) => void;
}

/** Scans frames from the camera until a QR code shows up. */
function CameraScanner({ onText, onUnavailable }: { onText: (text: string) => void; onUnavailable: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const onTextRef = useRef(onText);

  useEffect(() => {
    onTextRef.current = onText;
  }, [onText]);

  useEffect(() => {
    let stream: MediaStream | undefined;
    let frame = 0;
    let stopped = false;

    const scan = () => {
      const video = videoRef.current;

      if (stopped || !video) {
        return;
      }

      if (video.readyState >= 2 && video.videoWidth) {
        const text = decodeQr(video, video.videoWidth, video.videoHeight);

        if (text) {
          onTextRef.current(text);
          return;
        }
      }

      frame = requestAnimationFrame(scan);
    };

    navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode: "environment" } })
      .then((media) => {
        stream = media;

        if (stopped || !videoRef.current) {
          media.getTracks().forEach((track) => track.stop());
          return;
        }

        videoRef.current.srcObject = media;
        videoRef.current.play().catch(() => undefined);
        frame = requestAnimationFrame(scan);
      })
      .catch(onUnavailable);

    return () => {
      stopped = true;
      cancelAnimationFrame(frame);
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, [onUnavailable]);

  return <video ref={videoRef} className="w-full rounded-lg bg-black aspect-video object-cover" muted playsInline />;
}

/**
 * The BHYT card of a clinic visit (D5): read from the card's QR, by camera or from a picture, decoded
 * by the server, then used for this booking and filed on the patient's record.
 */
export default function InsuranceCardField({ value, record, onChange }: IProps) {
  //! state
  const { t, i18n } = useTranslation();
  const { open, shouldRender, toggle } = useToggle();
  const [mode, setMode] = useState<"CHOOSE" | "CAMERA">("CHOOSE");
  const [card, setCard] = useState<IHealthInsuranceCard>();
  const [error, setError] = useState<string>("");
  const [isDecoding, setIsDecoding] = useState<boolean>(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const uiLang = i18n.resolvedLanguage ?? "vi";
  const hasCamera = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;

  //! function
  const close = () => {
    toggle();
    setMode("CHOOSE");
    setCard(undefined);
    setError("");
  };

  const onText = useCallback(
    async (text: string | null) => {
      setMode("CHOOSE");

      if (!text || !isInsuranceQR(text)) {
        setError(t("booking.insurance.wrongQr"));
        return;
      }

      try {
        setIsDecoding(true);
        setError("");
        const resp = await patientInsuranceService.decodeHealthInsurance(text, uiLang);

        setCard({ ...resp.data, raw: text.trim() });
      } catch (decodeError) {
        setError(t("booking.genericError"));
      } finally {
        setIsDecoding(false);
      }
    },
    [t, uiLang],
  );

  const onCameraUnavailable = useCallback(() => {
    setMode("CHOOSE");
    setError(t("booking.insurance.noCamera"));
  }, [t]);

  const use = () => {
    if (!card) {
      return;
    }

    onChange(card);

    // Filed on the record as the app does; the booking does not depend on it.
    if (record?.code) {
      patientInsuranceService.saveHealthInsurance(card, record.code, uiLang).catch(() => undefined);
    }

    close();
  };

  //! render
  const renderCard = (insurance: IHealthInsuranceCard) => {
    const rows: [string, string | undefined][] = [
      [t("booking.insurance.name"), insurance.name],
      [t("booking.insurance.code"), insurance.code],
      [t("booking.insurance.dob"), insurance.dob],
      [t("booking.insurance.gender"), insurance.gender],
      [t("booking.insurance.address"), insurance.address],
      [t("booking.insurance.expired"), insurance.expired],
      [t("booking.insurance.facility"), insurance.health_insurance?.name],
      [t("booking.insurance.benefit"), insurance.health_insurance_benefit?.description],
    ];

    return (
      <div className="flex flex-col gap-2">
        {rows
          .filter(([, text]) => !!text)
          .map(([label, text]) => (
            <div key={label} className="flex justify-between gap-4 text-sm font-['Inter']">
              <span className="text-common-subtext shrink-0">{label}</span>
              <span className="text-common-maintext text-right">{text}</span>
            </div>
          ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="text-common-maintext text-sm font-medium font-['Inter']">{t("booking.insurance.title")}</span>

      {value ? (
        <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-common-divider bg-common-white">
          <span className="text-common-maintext text-sm font-medium font-['Inter']">{value.code}</span>
          <button type="button" aria-label={t("Delete")} className="text-common-subtext" onClick={() => onChange(null)}>
            <CommonIcons.CloseOutlined />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={toggle}
          className="w-full text-left px-3 py-2 rounded-lg border border-common-divider bg-common-white text-common-subtext text-sm font-['Inter'] hover:border-common-primary"
        >
          {t("booking.insurance.placeholder")}
        </button>
      )}

      {shouldRender && (
        <CommonStyles.ModalConfirmationUI
          open={open}
          toggle={close}
          type="normal"
          title={t("booking.insurance.title")}
          hiddenAction={!card}
          okText={t("booking.insurance.use")}
          cancelText={t("Cancel")}
          onConfirm={use}
          content={
            <div className="flex flex-col gap-3">
              {card ? renderCard(card) : null}

              {!card && mode === "CAMERA" ? <CameraScanner onText={onText} onUnavailable={onCameraUnavailable} /> : null}

              {!card && mode === "CHOOSE" ? (
                <>
                  <span className="text-common-subtext text-sm font-['Inter']">{t("booking.insurance.scanHint")}</span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {hasCamera ? (
                      <CommonStyles.ButtonUI className="h-12" icon={<CameraOutlined />} loading={isDecoding} onClick={() => setMode("CAMERA")}>
                        {t("booking.insurance.camera")}
                      </CommonStyles.ButtonUI>
                    ) : null}
                    <CommonStyles.ButtonUI
                      className="h-12 bg-common-primary100 text-common-primary hover:!bg-common-primary200 hover:!text-common-primary"
                      icon={<PictureOutlined />}
                      loading={isDecoding}
                      onClick={() => fileRef.current?.click()}
                    >
                      {t("booking.insurance.upload")}
                    </CommonStyles.ButtonUI>
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (event) => {
                      const file = event.target.files?.[0];

                      event.target.value = "";

                      if (file) {
                        onText(await decodeQrFromFile(file));
                      }
                    }}
                  />
                </>
              ) : null}

              {error ? <span className="text-common-red500 text-sm font-['Inter']">{error}</span> : null}
            </div>
          }
        />
      )}
    </div>
  );
}
